import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useSearchParams } from 'react-router';
import { CircleAlert, CopyPlus, Database, Info, Layers, Pencil, Plus, Search, Trash2 } from '@/components/icons';
import { APP_URL_KEY, testNameKey, type CollectionView, type VariableInput } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { VariableView } from '@/lib/types';
import { collectionCoverage, type CoverageGap } from '@/lib/testData';
import { Badge, Button, Card, Dialog, Field, Input, useConfirm, useToast } from '@/components/ui';
import { BuiltinValuesHelp, VariablesManager } from './VariablesManager';
import { SHARED_VALUES_LABEL } from './RunCollectionPicker';

type CollectionDialogState = { mode: 'new' } | { mode: 'edit'; collection: CollectionView } | { mode: 'copy'; collection: CollectionView } | null;

function CollectionDialog({
  state,
  taken,
  onClose,
  onSubmit,
  pending,
  error,
}: {
  state: CollectionDialogState;
  taken: CollectionView[];
  onClose: () => void;
  onSubmit: (input: { name: string; description: string | null }) => void;
  pending: boolean;
  error: unknown;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    if (!state) return;
    setName(state.mode === 'edit' ? state.collection.name : state.mode === 'copy' ? `${state.collection.name} copy` : '');
    setDescription(state.mode === 'new' ? '' : (state.collection.description ?? ''));
    setShowErrors(false);
  }, [state]);

  const editingId = state?.mode === 'edit' ? state.collection.id : null;
  const clash = !!name.trim() && taken.some((c) => c.id !== editingId && testNameKey(c.name) === testNameKey(name));
  const nameError = !name.trim() ? 'Enter a name, for example Staging' : clash ? 'A collection with this name already exists' : undefined;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setShowErrors(true);
    if (!nameError) onSubmit({ name: name.trim(), description: description.trim() || null });
  };
  const title =
    state?.mode === 'edit' ? `Edit ${state.collection.name}` : state?.mode === 'copy' ? `Duplicate ${state.collection.name}` : 'New test data collection';
  const help =
    state?.mode === 'copy'
      ? 'Creates a new collection with the same values (secrets stay hidden). Change what differs, such as APP_URL.'
      : 'A named set of values, usually one per environment. Pick it when you run tests, groups or flows, and its values replace the shared values with the same name.';

  return (
    <Dialog
      open={!!state}
      onClose={onClose}
      size="sm"
      title={title}
      description={help}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="collection-form" loading={pending}>
            {state?.mode === 'edit' ? 'Save' : state?.mode === 'copy' ? 'Duplicate' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="collection-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Name" error={showErrors ? nameError : undefined} hint="For example Dev, Staging or Preprod.">
          {(id) => <Input id={id} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} invalid={showErrors && !!nameError} autoFocus />}
        </Field>
        <Field label="Description (optional)">
          {(id) => (
            <Input id={id} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder="Which environment is this?" />
          )}
        </Field>
        {error ? (
          <p
            role="alert"
            className="rounded-field bg-danger-subtle p-3 text-sm text-danger-text ring-1 ring-danger-line ring-inset"
          >
            {errorMessage(error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

/** Shows a search box once the list is longer than this. */
const SEARCH_FROM = 6;
/** Names with no value shown before "Show all". */
const GAPS_SHOWN = 12;

/**
 * Left column: "Shared values" on its own (they are not an environment, they apply to every run),
 * then the collections: the organization's and the project's, each sorted by name, with search and
 * arrow-key navigation. The list scrolls on its own, so 20+ environments stay manageable. Below them, the
 * built-in values ({{$uuid}}...) in a collapsed card.
 */
function CollectionList({
  scope,
  collections,
  selectedId,
  defaultCount,
  onSelect,
  onCreate,
}: {
  scope: 'ORG' | 'PROJECT';
  collections: CollectionView[];
  selectedId: string | null;
  defaultCount: number;
  onSelect: (id: string | null) => void;
  onCreate?: () => void;
}) {
  const [query, setQuery] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const q = query.trim().toLowerCase();
  const shown = q ? collections.filter((c) => c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)) : collections;
  // When the page opens on a collection far down a long list (a link, a reload), show it near the top
  // of the list. Only the list scrolls, never the page; clicking an item does not move the list.
  useEffect(() => {
    const box = listRef.current;
    const el = box?.querySelector<HTMLElement>('[aria-current="true"]');
    // The list is the item's offset parent (relative); keep about two items above it.
    if (box && el) box.scrollTop = Math.max(0, el.offsetTop - 2 * el.offsetHeight);
  }, [collections.length]);

  const groups =
    scope === 'PROJECT'
      ? [
          {
            label: 'Organization',
            items: shown.filter((c) => c.scope === 'ORG'),
          },
          {
            label: 'This project',
            items: shown.filter((c) => c.scope === 'PROJECT'),
          },
        ]
      : // The organization page has one group; the card title already names it.
        [{ label: null, items: shown }];

  const visibleGroups = groups.filter((g) => g.items.length > 0).length;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button[data-item]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (i < 0) return;
    e.preventDefault();
    items[Math.min(Math.max(i + (e.key === 'ArrowDown' ? 1 : -1), 0), items.length - 1)]?.focus();
  };

  const item = (id: string, name: string, count: number, title?: string) => {
    const active = selectedId === id;
    return (
      <li key={id}>
        <button
          type="button"
          data-item
          onClick={() => onSelect(id)}
          aria-current={active ? 'true' : undefined}
          title={title}
          className={clsx(
            'flex w-full items-center gap-2 rounded-field px-2.5 py-2 text-left text-sm transition focus:outline-none focus-visible:shadow-focus focus-visible:ring-1 focus-visible:ring-primary',
            active
              ? 'bg-primary-subtle font-semibold text-primary ring-1 ring-primary/40 ring-inset'
              : 'text-fg-secondary hover:bg-fg/8 hover:text-fg',
          )}
        >
          <span className="min-w-0 flex-1 truncate">{name}</span>
          <span
            className={clsx('shrink-0 text-xs tabular-nums', active ? 'text-primary' : 'text-fg-muted')}
            aria-label={`${count} values`}
          >
            {count}
          </span>
        </button>
      </li>
    );
  };

  const sharedActive = selectedId === null;
  return (
    <div className="space-y-4 lg:sticky lg:top-4">
      <button
        type="button"
        onClick={() => onSelect(null)}
        aria-current={sharedActive ? 'true' : undefined}
        className={clsx(
          'flex w-full items-center gap-3 rounded-card px-4 py-3 text-left ring-1 ring-inset transition focus:outline-none focus-visible:shadow-focus focus-visible:ring-primary',
          sharedActive
            ? 'bg-primary-subtle ring-primary'
            : 'bg-surface ring-border hover:ring-primary/40',
        )}
      >
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
          <Database className="size-4" aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={clsx('block text-sm font-semibold', sharedActive ? 'text-primary' : 'text-fg')}>
            {SHARED_VALUES_LABEL}
          </span>
          <span className="block text-xs text-fg-muted">Used in every environment</span>
        </span>
        <span className={clsx('shrink-0 text-xs tabular-nums', sharedActive ? 'text-primary' : 'text-fg-muted')} aria-label={`${defaultCount} values`}>
          {defaultCount}
        </span>
      </button>

      <Card>
        <div className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
          <Layers className="size-4 text-primary" aria-hidden />
          <h2 className="flex-1 text-sm font-semibold text-fg">
            Collections <span className="font-normal text-fg-muted">({collections.length})</span>
          </h2>
          {onCreate && (
            <Button size="sm" variant="ghost" onClick={onCreate} icon={<Plus className="size-4" />}>
              New
            </Button>
          )}
        </div>
        {collections.length > SEARCH_FROM && (
          <div className="relative border-b border-border-subtle px-3 py-2.5">
            <Search className="pointer-events-none absolute top-1/2 left-7 size-3.5 -translate-y-1/2 text-fg-muted" aria-hidden />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find a collection…"
              aria-label="Find a collection"
              className="pl-10"
            />
          </div>
        )}
        <div ref={listRef} onKeyDown={onKeyDown} className="relative max-h-72 overflow-y-auto p-2 lg:max-h-[max(16rem,calc(100vh-33rem))]">
          {collections.length === 0 ? (
            <p className="px-2.5 pt-3 pb-1 text-xs leading-relaxed text-fg-muted">
              Test the same app in several environments? Create one collection per environment (Dev, Staging, Preprod) with only the values that differ;
              everything else comes from the shared values. Pick the collection when you run.
            </p>
          ) : shown.length === 0 ? (
            <p className="px-2.5 pt-3 pb-1 text-xs text-fg-muted" role="status">
              No collection matches “{query.trim()}”.
            </p>
          ) : (
            groups
              .filter((g) => g.items.length > 0)
              .map((g) => (
                <div key={g.label ?? 'all'} className="mt-3 first:mt-1">
                  {/* Headings only tell groups apart; a single group is already named by the card title. */}
                  {g.label && visibleGroups > 1 && (
                    <div className="px-2.5 pb-1 text-[11px] font-semibold tracking-label text-fg-muted uppercase">
                      {g.label} · {g.items.length}
                    </div>
                  )}
                  <ul className="space-y-0.5">{g.items.map((c) => item(c.id, c.name, c.valueCount, c.description ?? undefined))}</ul>
                </div>
              ))
          )}
        </div>
      </Card>

      <BuiltinValuesHelp />
    </div>
  );
}

/**
 * Test data with collections: the shared values and the collections (Dev, Staging...) beside the
 * values of the chosen one, which names the project's tests use that this choice can't fill, and managing
 * collections. The chosen collection is kept in the address (?collection=), so a reload or a shared
 * link opens it again. On a project page the organization's collections are listed too; their
 * organization-wide values are read-only there, but the project can add its own values to them.
 */
export function TestDataWithCollections({
  scope,
  orgId,
  projectId,
  variables,
  canEdit,
  canCreateCollections,
  createVariable,
  variableQueryKeys,
  sharedDescription,
}: {
  scope: 'ORG' | 'PROJECT';
  orgId: string;
  projectId?: string;
  variables: VariableView[];
  canEdit: boolean;
  canCreateCollections: boolean;
  createVariable: (input: VariableInput) => Promise<VariableView>;
  variableQueryKeys: unknown[][];
  sharedDescription: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const { canModify } = useAuth();
  const [params, setParams] = useSearchParams();
  const collectionsKey = scope === 'PROJECT' ? ['project-collections', projectId] : ['org-collections', orgId];
  const collections = useQuery({
    queryKey: collectionsKey,
    queryFn: () => (scope === 'PROJECT' ? api.projectCollections(projectId!) : api.orgCollections(orgId)),
  });
  const [dialog, setDialog] = useState<CollectionDialogState>(null);
  const [addRequest, setAddRequest] = useState<{ key: string } | null>(null);
  const [showAllGaps, setShowAllGaps] = useState(false);

  const list = collections.data ?? [];
  const selected = list.find((c) => c.id === params.get('collection')) ?? null;
  const select = (id: string | null) => {
    setShowAllGaps(false);
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('collection', id);
        else next.delete('collection');
        return next;
      },
      { replace: true },
    );
  };
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: collectionsKey });
    // Org collections show up in every project, and their values in project lists.
    if (scope === 'ORG') qc.invalidateQueries({ queryKey: ['project-collections'] });
    variableQueryKeys.forEach((queryKey) => qc.invalidateQueries({ queryKey }));
  };

  const save = useMutation({
    mutationFn: (input: { name: string; description: string | null }) => {
      if (dialog?.mode === 'edit') return api.updateCollection(dialog.collection.id, input);
      if (dialog?.mode === 'copy') return api.copyCollection(dialog.collection.id, input);
      return scope === 'PROJECT' ? api.createProjectCollection(projectId!, input) : api.createOrgCollection(orgId, input);
    },
    onSuccess: (c) => {
      toast.success(dialog?.mode === 'edit' ? `${c.name} saved` : `${c.name} created`);
      invalidate();
      select(c.id);
      setDialog(null);
    },
  });

  const remove = useMutation({
    mutationFn: (c: CollectionView) => api.deleteCollection(c.id),
    onSuccess: (_, c) => {
      toast.success(`${c.name} deleted`);
      select(null);
      invalidate();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const manageAction = (c: CollectionView) => (c.scope === 'ORG' ? 'variables.org.manage' : 'test.write');
  // Organization collections are changed on the organization's Test data page.
  const canChange = (c: CollectionView) => c.scope === scope && canModify(manageAction(c), c.createdBy.id, orgId);
  const canDuplicate = (c: CollectionView) => c.scope === scope && canCreateCollections;

  const shown = variables.filter((v) => (v.collectionId ?? null) === (selected?.id ?? null));
  const hasAppUrl = shown.some((v) => v.key === APP_URL_KEY && !v.overridden);
  const defaultCount = variables.filter((v) => !v.collectionId && !v.overridden).length;

  // What the project's tests need that this choice can't fill. Collections are independent: names that
  // only other collections have never count, and a name the shared values have is fine.
  const tests = useQuery({
    queryKey: ['tests', projectId],
    queryFn: () => api.tests(projectId!),
    enabled: scope === 'PROJECT' && !!projectId,
  });
  const coverage = useMemo(
    () => (scope === 'PROJECT' && tests.data ? collectionCoverage(tests.data, variables, selected?.id ?? null) : { missing: [], written: [] }),
    [scope, tests.data, variables, selected],
  );
  const gapChips = (gaps: CoverageGap[], kind: 'missing' | 'written') => (
    <ul className="mt-2 flex flex-wrap gap-1.5">
      {(showAllGaps ? gaps : gaps.slice(0, GAPS_SHOWN)).map((g) => {
        const usedBy = `Used by ${g.tests.slice(0, 5).join(', ')}${g.tests.length > 5 ? ` and ${g.tests.length - 5} more` : ''}.`;
        return (
          <li key={g.name}>
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => setAddRequest({ key: g.name })}
              title={`${usedBy}${canEdit ? ` Click to add it to ${selected?.name ?? 'the shared values'}.` : ''}`}
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-3 py-[5px] text-xs ring-1 ring-inset transition enabled:hover:ring-primary disabled:cursor-default',
                kind === 'missing'
                  ? 'bg-warning-subtle text-warning ring-warning/40'
                  : 'bg-surface text-fg ring-border',
              )}
            >
              {canEdit && <Plus className="size-3" aria-hidden />}
              <span className="font-mono">{g.name}</span>
              <span className="opacity-70">
                · {g.tests.length} test{g.tests.length === 1 ? '' : 's'}
              </span>
              <span className="sr-only">{usedBy}</span>
            </button>
          </li>
        );
      })}
      {gaps.length > GAPS_SHOWN && (
        <li>
          <button type="button" onClick={() => setShowAllGaps((s) => !s)} className="rounded-lg px-2 py-1 text-xs font-medium text-primary hover:underline">
            {showAllGaps ? 'Show fewer' : `Show all ${gaps.length}`}
          </button>
        </li>
      )}
    </ul>
  );

  const openDialog = (state: CollectionDialogState) => {
    save.reset();
    setDialog(state);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)] lg:items-start">
      <CollectionDialog
        state={dialog}
        taken={list}
        onClose={() => setDialog(null)}
        onSubmit={(input) => save.mutate(input)}
        pending={save.isPending}
        error={save.error}
      />

      <CollectionList
        scope={scope}
        collections={list}
        selectedId={selected?.id ?? null}
        defaultCount={defaultCount}
        onSelect={select}
        onCreate={canCreateCollections ? () => openDialog({ mode: 'new' }) : undefined}
      />

      <div className="min-w-0 space-y-4">
        <Card>
          <div className="flex flex-wrap items-start gap-3 px-5 py-4">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary">
              {selected ? <Layers className="size-[18px]" /> : <Database className="size-[18px]" />}
            </span>
            <div className="min-w-[12rem] flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="min-w-0 break-words text-lg font-semibold text-fg">{selected?.name ?? SHARED_VALUES_LABEL}</h2>
                {selected && scope === 'PROJECT' && (
                  <Badge tone={selected.scope === 'ORG' ? 'info' : 'neutral'}>
                    {selected.scope === 'ORG' ? 'Organization collection' : 'Project collection'}
                  </Badge>
                )}
              </div>
              <p className="mt-0.5 text-sm text-fg-secondary">
                {selected
                  ? (selected.description ?? `Runs that pick ${selected.name} use these values; anything not set here comes from the shared values.`)
                  : 'Used in every environment. When a run picks a collection, the collection’s value wins for any name it also has. Runs without a collection use only these.'}
              </p>
            </div>
            {selected && (
              <div className="flex shrink-0 gap-1">
                {canDuplicate(selected) && (
                  <Button size="sm" variant="ghost" icon={<CopyPlus className="size-4" />} onClick={() => openDialog({ mode: 'copy', collection: selected })}>
                    Duplicate
                  </Button>
                )}
                {canChange(selected) && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Pencil className="size-4" />}
                      aria-label={`Edit ${selected.name}`}
                      title="Rename or describe"
                      onClick={() => openDialog({ mode: 'edit', collection: selected })}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Trash2 className="size-4 text-danger-text" />}
                      aria-label={`Delete ${selected.name}`}
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Delete ${selected.name}?`,
                          message: `${selected.name} and all its values${selected.scope === 'ORG' ? ', including every project’s,' : ''} will be deleted. Past reports keep its name.`,
                          confirmLabel: 'Delete collection',
                        });
                        if (ok) remove.mutate(selected);
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>

          {(coverage.missing.length > 0 || coverage.written.length > 0) && (
            <div className="space-y-3 border-t border-border-subtle px-5 py-3.5 text-sm">
              {coverage.missing.length > 0 && (
                <div className="flex items-start gap-2">
                  <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-fg">
                      <span className="font-medium">
                        Your tests use {coverage.missing.length === 1 ? 'a name that has' : `${coverage.missing.length} names that have`} no value{' '}
                        {selected ? `in ${selected.name} or the shared values` : 'in the shared values'}.
                      </span>{' '}
                      {selected ? `Run with ${selected.name}, these` : 'Run without a collection, these'} tests stop with an error. Add{' '}
                      {coverage.missing.length === 1 ? 'it' : 'them'} here
                      {selected ? ' or to the shared values' : ''}, or write a value in the test step.
                    </p>
                    {gapChips(coverage.missing, 'missing')}
                  </div>
                </div>
              )}
              {coverage.written.length > 0 && (
                <div className="flex items-start gap-2">
                  <Info className="mt-0.5 size-4 shrink-0 text-link" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-fg-secondary">
                      No value {selected ? `in ${selected.name} or the shared values` : 'in the shared values'} for{' '}
                      {coverage.written.length === 1 ? 'this name' : 'these names'}, so runs use the value written in the test.
                    </p>
                    {gapChips(coverage.written, 'written')}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        <VariablesManager
          key={selected?.id ?? 'default'}
          variables={shown}
          scope={scope}
          orgId={orgId}
          projectId={projectId}
          canEdit={canEdit}
          create={(input) => createVariable({ ...input, collectionId: selected?.id ?? null })}
          queryKeys={[...variableQueryKeys, collectionsKey]}
          addRequest={addRequest}
          title="Values"
          description={
            selected
              ? `When a run picks ${selected.name}, these replace shared values with the same name.${
                  !hasAppUrl ? ` Tip: add ${APP_URL_KEY} to run against this environment’s address.` : ''
                }`
              : sharedDescription
          }
        />
      </div>
    </div>
  );
}
